import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { ApplicationHttpClient } from '@app/core/http';

import { Credential, Tenant, ErrorMessage } from '@app/core/models';
import { CompanyService, InsuranceCodeStateService } from '@app/core/services';
import { MyCompany } from '../models/myCompany';

const CREDENTIAL_STORAGE = 'CREDENTIALS';
const TENANT_STORAGE = 'TENANTS';
const FOREIGNER_STORAGE = 'FOREIGNER_STORAGE';
const REMEMBERME_STORAGE = 'REMEMBERME';
const ERRORMESSAGE_STORAGE = 'ERRORMESSAGE';
var company : MyCompany;
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private credentialSubject: BehaviorSubject<Credential>;
  public credentials: Observable<Credential>;
  public tenantSubject: BehaviorSubject<Tenant>;
  public tenants: Observable<Tenant>;
  public errorMessage: Observable<ErrorMessage>;
  public errorMessageSubject: BehaviorSubject<ErrorMessage>;

  constructor(private http: ApplicationHttpClient, private companyService: CompanyService, private insuranceCodeStateService: InsuranceCodeStateService) {
    this.credentialSubject = new BehaviorSubject<Credential>(JSON.parse(localStorage.getItem(CREDENTIAL_STORAGE)));
    this.credentials = this.credentialSubject.asObservable();
    this.tenantSubject = new BehaviorSubject<Tenant>(JSON.parse(localStorage.getItem(TENANT_STORAGE)));
    this.errorMessageSubject = new BehaviorSubject<ErrorMessage>(JSON.parse(localStorage.getItem(ERRORMESSAGE_STORAGE)));
    this.tenants = this.tenantSubject.asObservable();
    this.errorMessage = this.errorMessageSubject.asObservable();
  }

  public get currentCredentials(): Credential {
    return JSON.parse(localStorage.getItem(CREDENTIAL_STORAGE));
  }

  public get getErrorMessageSubject(): ErrorMessage {
    return JSON.parse(localStorage.getItem(ERRORMESSAGE_STORAGE));
  }

  public get currentTenant(): Tenant {
    return JSON.parse(localStorage.getItem(TENANT_STORAGE));
  }

  public getTenant(name: string): Observable<any> {
    return this.http.get(`/api/abp/multi-tenancy/tenants/by-name/${name}`).pipe(
      map(data => {
        return this.storeTenant(data);
      })
    );;
  }


  public login(userId: string, password: string, isForeigner: any): Observable<any> {
    const params = new URLSearchParams();
    params.append('grant_type', 'password');
    params.append('client_id', 'Insurance_App');
    params.append('scope', 'offline_access Insurance');
    params.append('issuer', 'https://localhost:44322');
    params.append('username', userId);
    params.append('password', password);

    
    return this.http.post('/connect/token', params.toString(), {
      headers: {
         'Content-Type': 'application/x-www-form-urlencoded',
      }
    }).pipe(
      map(data => {
        return this.addTokenToStoreTenant(data);
      })
    );
  }

  public refreshToken(refreshToken: string): Observable<any> {
    const params = new URLSearchParams();
    params.append('grant_type', 'refresh_token');
    params.append('client_id', 'Insurance_App');
    params.append('refresh_token', refreshToken);

    
    return this.http.post('/connect/token', params.toString(), {
      headers: {
         'Content-Type': 'application/x-www-form-urlencoded',
      }
    }).pipe(
      map(data => {
        return this.addTokenToStoreTenant(data);
      })
    );
  }

  public getMyProfile(): Observable<any> {
    return this.http.get(`/api/account/my-profile`, {});
  }

  public getApplicationConfiguration(companyInfo:any): Observable<any> {
    return this.http.get(`/api/abp/application-configuration`, {}).pipe(
      map(data => {
        return this.addConfigurationToStoreTenant(data,companyInfo);
      })
    );
  }

  public logout() {
    const params = new URLSearchParams();
    params.append('token_type_hint', 'access_token');
    params.append('client_id', 'Insurance_App');
    params.append('token', this.currentTenant.token);
    this.http.post('/connect/revocat', params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }).toPromise();
    localStorage.removeItem(CREDENTIAL_STORAGE);
    this.credentialSubject.next(null);
    localStorage.removeItem(TENANT_STORAGE);
    this.tenantSubject.next(null);
    this.insuranceCodeStateService.clearInsuranceCode();
  }

  public requestResetPassword(body) {
    return this.http.post('/api/app/my-account/send-reset-password', body);
  }

  public getUserByToken(token: string) {
    return this.http.get('/session/account-info', {
      params: { token }
    });
  }

  public resetPassword(passWordInfo: any) {
    return this.http.post(`/api/account/my-profile/change-password`, passWordInfo);
  }

  public getCredentialToken() {
    const currentCredentials = this.currentCredentials;

    if (currentCredentials && currentCredentials.token) {
      return currentCredentials.token;
    }
  }

  public getCurrentTenantToken() {
    const currentTenant = this.currentTenant;

    if (currentTenant && currentTenant.token) {
      return currentTenant.token;
    }
  }

  public storeCredentials(data) {
    const credentials: Credential = {
      token: data.token,
      userId: data.userId,
      username: data.username,
      email: data.email,
      currentDate: data.currentDate,
      companyInfo: data.company,
      role: data.role
    };

    localStorage.setItem(CREDENTIAL_STORAGE, JSON.stringify(credentials));

    this.credentialSubject.next(credentials);

    return credentials;
  }

  public addTokenToStoreTenant(data) {
    const currentTenant = JSON.parse(localStorage.getItem(TENANT_STORAGE));
    currentTenant.token = data.access_token;
    currentTenant.expires_in = data.expires_in;
    currentTenant.refresh_token = data.refresh_token;
    currentTenant.token_type = data.token_type;
    currentTenant.companyInfo = data.companyInfo || currentTenant.companyInfo;
    localStorage.setItem(TENANT_STORAGE, JSON.stringify(currentTenant));
    this.tenantSubject.next(currentTenant);
    return currentTenant;
  }
  public addConfigurationToStoreTenant(data,companyInfo) {
    
    const currentTenant = JSON.parse(localStorage.getItem(TENANT_STORAGE));
    currentTenant.configuration = data;
     currentTenant.companyInfo = companyInfo;
    localStorage.setItem(TENANT_STORAGE, JSON.stringify(currentTenant));
    this.setLocalization(data);
    this.tenantSubject.next(currentTenant);
    return currentTenant;
  }

  private setLocalization(data) {
    if (data && data.localization && data.localization.values && data.localization.values.Insurance) {
      let messageError = data.localization.values.Insurance;
      localStorage.setItem(ERRORMESSAGE_STORAGE, JSON.stringify(messageError));
      this.errorMessageSubject.next(messageError);
    }
  }

  public storeTenant(data) {
    const tenant: Tenant = {
      isActive: data.isActive,
      name: data.name,
      success: data.success,
      tenantId: data.tenantId,
    };

    localStorage.setItem(TENANT_STORAGE, JSON.stringify(tenant));

    this.tenantSubject.next(tenant);

    return tenant;
  }

  public updateCompanyInStorage(companyInfo) {
    const credentials = JSON.parse(localStorage.getItem(CREDENTIAL_STORAGE));
    credentials.companyInfo = companyInfo;
    localStorage.setItem(CREDENTIAL_STORAGE, JSON.stringify(credentials));
    this.credentialSubject.next(credentials);
  }

  public saveRememberMe(objectLoginFrom) {
    localStorage.setItem(FOREIGNER_STORAGE, JSON.stringify(objectLoginFrom));
    if (objectLoginFrom.remember) {
      localStorage.setItem(REMEMBERME_STORAGE, JSON.stringify(objectLoginFrom));
    } else {
      localStorage.removeItem(REMEMBERME_STORAGE);
    }
  }

  public getForeigner() {
    return JSON.parse(localStorage.getItem(FOREIGNER_STORAGE));
  }

  public getAutoLogin() {
    return JSON.parse(localStorage.getItem(REMEMBERME_STORAGE));
  }

  public getMessageError(error: any) {
    if (!error || !error.error) {
      return "Có lỗi trong quá trình thao tác vui lòng kiểm tra lại";
    }
    if (!error.error.code || error.error.code === '') {
      return error.error.message;
    }
    let message = this.getErrorMessageSubject['ErrorCode-' + error.error.code];
    if (!message || message === '') {
      message = error.error.message;
    }
    return message;
  }
}
